
import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Badge,
  Card,
  CardBody,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  SimpleGrid,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Code
} from '@chakra-ui/react';
import { fetchWithAuth } from '../utils/fetchWithAuth';

const AdminCoreProducts = ({ standalone = false }) => {
  const [categories, setCategories] = useState({});
  const [coreProducts, setCoreProducts] = useState({});
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const Wrapper = React.Fragment;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch categories
        const categoriesResponse = await fetchWithAuth('/api/core-features-registry/categories');
        const categoriesData = await categoriesResponse.json();
        
        // Fetch core products
        const productsResponse = await fetchWithAuth('/api/core-features-registry/core-products');
        const productsData = await productsResponse.json();
        
        // Fetch features
        const featuresResponse = await fetchWithAuth('/api/core-features-registry/features');
        const featuresData = await featuresResponse.json();
        
        setCategories(categoriesData.categories || {});
        setCoreProducts(productsData.core_products || {});
        setFeatures(featuresData.features || {});
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return (
    <Wrapper>
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Spinner size="xl" />
      </Box>
    </Wrapper>
  );

  if (error) return (
    <Wrapper>
      <Alert status="error">
        <AlertIcon />
        Error loading data: {error}
      </Alert>
    </Wrapper>
  );

  return (
    <Wrapper>
      <Box width="100%" mx="auto" bg="white" p={6}>
        <VStack align="flex-start" spacing={6} width="100%">
          <Box>
            <Heading size="xl" mb={2}>Core Products Overview</Heading>
            <Text color="gray.600">
              Categories, products, and their associated features
            </Text>
          </Box>

          {/* Categories Overview */}
          <Box width="100%">
            <Heading size="lg" mb={4}>Product Categories</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {Object.entries(categories).map(([categoryId, category]) => (
                <Card key={categoryId} border="1px" borderColor="gray.200">
                  <CardBody>
                    <VStack align="start" spacing={3}>
                      <HStack>
                        <Heading size="md">{category.name}</Heading>
                        <Badge colorScheme="blue">{category.products.length} products</Badge>
                      </HStack>
                      <Text fontSize="sm" color="gray.600">
                        {category.description}
                      </Text>
                      <Box>
                        <Text fontWeight="bold" fontSize="sm" mb={2}>Products:</Text>
                        {category.products.map((productId) => (
                          <Badge key={productId} mr={2} mb={1} variant="outline">
                            {coreProducts[productId]?.name || productId}
                          </Badge>
                        ))}
                      </Box>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </Box>

          {/* Core Products Details */}
          <Box width="100%">
            <Heading size="lg" mb={4}>Core Products Details</Heading>
            <Accordion allowMultiple>
              {Object.entries(coreProducts).map(([productId, product]) => {
                const productFeatures = Object.entries(features).filter(
                  ([_, feature]) => feature.core_product === productId
                );

                return (
                  <AccordionItem key={productId}>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <HStack>
                          <Heading size="md">{product.name}</Heading>
                          <Badge colorScheme="green">{productFeatures.length} features</Badge>
                          <Badge variant="outline">{product.category}</Badge>
                        </HStack>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      <VStack align="start" spacing={4}>
                        <Text>{product.description}</Text>
                        
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                          <Box>
                            <Text fontWeight="bold" mb={2}>Architecture:</Text>
                            <VStack align="start" spacing={2}>
                              {product.main_component && (
                                <HStack>
                                  <Text fontSize="sm">Main Component:</Text>
                                  <Code fontSize="sm">{product.main_component}</Code>
                                </HStack>
                              )}
                              {product.component_path && (
                                <HStack>
                                  <Text fontSize="sm">Component Path:</Text>
                                  <Code fontSize="xs">{product.component_path}</Code>
                                </HStack>
                              )}
                              {product.embedded_component && (
                                <HStack>
                                  <Text fontSize="sm">Embedded Component:</Text>
                                  <Code fontSize="sm">{product.embedded_component}</Code>
                                </HStack>
                              )}
                            </VStack>
                          </Box>
                          <Box>
                            <Text fontWeight="bold" mb={2}>Configuration:</Text>
                            <VStack align="start" spacing={2}>
                              <HStack>
                                <Text fontSize="sm">Workflow:</Text>
                                <Badge colorScheme="blue">{product.workflow || 'default'}</Badge>
                              </HStack>
                              <HStack>
                                <Text fontSize="sm">Icon:</Text>
                                <Badge colorScheme="gray">{product.icon || 'none'}</Badge>
                              </HStack>
                              <HStack>
                                <Text fontSize="sm">Core Features:</Text>
                                <Badge colorScheme="green">{product.core_features?.length || 0}</Badge>
                              </HStack>
                            </VStack>
                          </Box>
                        </SimpleGrid>

                        <Box width="100%">
                          <Text fontWeight="bold" mb={2}>Associated Features:</Text>
                          <Table size="sm" variant="striped">
                            <Thead>
                              <Tr>
                                <Th>Feature</Th>
                                <Th>Component</Th>
                                <Th>Capabilities</Th>
                                <Th>AI Enabled</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {productFeatures.map(([featureId, feature]) => (
                                <Tr key={featureId}>
                                  <Td>
                                    <VStack align="start" spacing={1}>
                                      <Text fontWeight="semibold">{feature.name}</Text>
                                      <Text fontSize="xs" color="gray.500">{featureId}</Text>
                                    </VStack>
                                  </Td>
                                  <Td>
                                    <Code fontSize="xs">{feature.component}</Code>
                                  </Td>
                                  <Td>
                                    <Badge variant="outline">
                                      {feature.capabilities?.length || 0} capabilities
                                    </Badge>
                                  </Td>
                                  <Td>
                                    {feature.ai_capabilities ? (
                                      <Badge colorScheme="green">Yes</Badge>
                                    ) : (
                                      <Badge colorScheme="gray">No</Badge>
                                    )}
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </Box>
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </Box>
        </VStack>
      </Box>
    </Wrapper>
  );
};

export default AdminCoreProducts;
